import { motion } from "framer-motion";
import { Heart, Code, MapPin, Mail, Globe, Megaphone } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function About() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  return (
    <>
      <Helmet>
        <title>About Us | Info Payangadi</title>
        <meta name="description" content="Learn more about Info Payangadi, the digital directory of Kalliasseri, and the team behind it." />
      </Helmet>

      {/* 🔥 FIX: Removed overflow-y-auto to allow natural scrolling 🔥 */}
      <div className="min-h-[100dvh] bg-[#F8FAFC] pb-24 selection:bg-blue-200 overflow-x-hidden relative">
        
        {/* 🔥 Premium Background Glows (Softer) 🔥 */}
        <div className="absolute top-[-10%] left-[-10%] w-[350px] h-[350px] bg-blue-400/15 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute top-[20%] right-[-10%] w-[350px] h-[350px] bg-cyan-400/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 md:pt-16 relative z-10">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-12 sm:space-y-16"
          >
            
            {/* 1. HERO SECTION */}
            <motion.div variants={itemVariants} className="text-center space-y-4 sm:space-y-5 px-2">
              <div className="inline-flex items-center justify-center p-4 bg-white text-blue-600 rounded-[1.5rem] mb-2 shadow-[0_8px_30px_rgb(37,99,235,0.08)] border border-blue-50/50">
                <MapPin size={32} strokeWidth={2.5} />
              </div>
              <h1 className="text-3xl sm:text-5xl font-black text-slate-800 tracking-tight leading-tight">
                INFO <span className="text-blue-600">PAYANGADI</span>
              </h1>
              <p className="text-base sm:text-lg text-slate-500 max-w-xl mx-auto leading-relaxed font-medium">
                കല്ല്യാശ്ശേരി നിയോജക മണ്ഡലത്തിലെ പ്രാദേശിക വിവരങ്ങൾ അടങ്ങിയ ഡിജിറ്റൽ ഡയറക്ടറി ആണ് ഇൻഫോ പഴയങ്ങാടി. തികച്ചും ലാഭരഹിതമായി (Non Profit) ആയി പ്രവർത്തിക്കുന്നു.
              </p>
            </motion.div>

            {/* 2. MISSION / FEATURES CARDS */}
            <motion.div variants={itemVariants} className="grid sm:grid-cols-2 gap-5 sm:gap-6">
              
              <div className="bg-white p-7 sm:p-10 rounded-[2.5rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 hover:shadow-[0_15px_40px_rgb(239,68,68,0.08)] transition-all duration-300 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-red-50/60 rounded-bl-[100px] -z-10 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="bg-red-50 w-max p-3.5 rounded-2xl mb-5">
                  <Heart className="text-red-500" size={26} strokeWidth={2.5} />
                </div>
                <h3 className="text-xl sm:text-2xl font-black text-slate-800 mb-3 tracking-tight">Our Vision</h3>
                <p className="text-slate-500 leading-relaxed text-sm sm:text-base font-medium">
                  നാട്ടിലെ ടൂറിസ്റ്റ് കേന്ദ്രങ്ങൾ, രക്തദാതാക്കളുടെ വിവരങ്ങൾ, മറ്റ് അത്യാവശ്യ സേവനങ്ങൾ എന്നിവ ഒറ്റ പ്ലാറ്റ്‌ഫോമിൽ ലഭ്യമാക്കുക വഴി ജനങ്ങൾക്ക് ഉപകാരപ്രദമാകുന്ന ഒരു ഡിജിറ്റൽ ഡയറക്ടറി സൃഷ്ടിക്കുക എന്നതാണ് ഞങ്ങളുടെ ലക്ഷ്യം.
                </p>
              </div>

              <div className="bg-white p-7 sm:p-10 rounded-[2.5rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 hover:shadow-[0_15px_40px_rgb(6,182,212,0.08)] transition-all duration-300 relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-32 h-32 bg-cyan-50/60 rounded-br-[100px] -z-10 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="bg-cyan-50 w-max p-3.5 rounded-2xl mb-5">
                  <Globe className="text-cyan-600" size={26} strokeWidth={2.5} />
                </div>
                <h3 className="text-xl sm:text-2xl font-black text-slate-800 mb-3 tracking-tight">Community Driven</h3>
                <p className="text-slate-500 leading-relaxed text-sm sm:text-base font-medium">
                  Payangadi Live-ന് കീഴിൽ പ്രവർത്തിക്കുന്ന ഈ പ്ലാറ്റ്‌ഫോം പൂർണ്ണമായും നാട്ടുകാരുടെ സഹകരണത്തോടെയാണ് മുന്നോട്ട് പോകുന്നത്. നിങ്ങളുടെ ഓരോരുത്തരുടെയും പിന്തുണയാണ് ഇതിന്റെ ശക്തി.
                </p>
              </div>

            </motion.div>

            {/* 3. TEAM SECTION */}
            <motion.div variants={itemVariants} className="pt-8 space-y-6 sm:space-y-8">
              
              <div className="text-center mb-8 px-4">
                <h2 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tight">Meet the Team</h2>
                <p className="text-slate-400 text-sm font-medium mt-1">The minds behind Info Payangadi</p>
              </div>

              {/* Developer Profile */}
              <div className="relative bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-800 via-slate-900 to-black rounded-[2.5rem] p-7 sm:p-10 shadow-[0_20px_40px_rgba(15,23,42,0.2)] border border-slate-700/50 text-white overflow-hidden group transition-transform duration-300 hover:-translate-y-1">
                
                <div className="absolute -top-16 -right-16 w-56 h-56 bg-white/5 rounded-full blur-2xl pointer-events-none group-hover:bg-white/10 transition-colors duration-500"></div>
                <div className="absolute -bottom-16 -left-16 w-56 h-56 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>

                <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
                  
                  <div className="space-y-4 flex-1">
                    <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-slate-800/80 backdrop-blur-md border border-slate-600/50 text-blue-300 text-[11px] font-bold uppercase tracking-wider">
                      <Code size={14} strokeWidth={2.5} /> Lead Developer
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tight">Varun Chengal</h2>
                    <p className="text-slate-300/90 leading-relaxed text-sm sm:text-base font-medium max-w-md">
                      Passionately crafting digital experiences. Developed and designed this platform to bring our community closer together.
                    </p>
                  </div>

                  <div className="flex flex-col gap-3 w-full sm:w-auto mt-2 sm:mt-0">
                    <a 
                      href="mailto:tovarunchengal@gmail.com"
                      className="flex items-center justify-center gap-2.5 px-6 py-3.5 bg-white/5 hover:bg-white/10 backdrop-blur-md rounded-2xl transition duration-300 border border-white/10 active:scale-95"
                    >
                      <Mail size={18} className="text-blue-300" strokeWidth={2.5} />
                      <span className="font-bold text-white text-sm tracking-wide">Contact Me</span>
                    </a>
                    <a 
                      href="https://www.varunchengal.in"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2.5 px-6 py-3.5 bg-blue-600 hover:bg-blue-500 rounded-2xl transition duration-300 shadow-[0_8px_20px_rgba(37,99,235,0.4)] border border-blue-500 active:scale-95"
                    >
                      <Globe size={18} className="text-white" strokeWidth={2.5} />
                      <span className="font-bold text-white text-sm tracking-wide">Visit Website</span>
                    </a>
                  </div>

                </div>
              </div>

              {/* Marketing Head Profile */}
              <div className="relative bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-indigo-900 via-slate-900 to-black rounded-[2.5rem] p-7 sm:p-10 shadow-[0_20px_40px_rgba(30,27,75,0.2)] border border-indigo-900/50 text-white overflow-hidden group transition-transform duration-300 hover:-translate-y-1 mt-6">
                
                <div className="absolute -top-16 -left-16 w-56 h-56 bg-white/5 rounded-full blur-2xl pointer-events-none group-hover:bg-white/10 transition-colors duration-500"></div>
                <div className="absolute -bottom-16 -right-16 w-56 h-56 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>

                <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
                  
                  <div className="space-y-4 flex-1">
                    <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-indigo-900/40 backdrop-blur-md border border-indigo-700/50 text-indigo-300 text-[11px] font-bold uppercase tracking-wider">
                      <Megaphone size={14} strokeWidth={2.5} /> Marketing Head
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tight">Anand B Prakash</h2>
                    <p className="text-slate-300/90 leading-relaxed text-sm sm:text-base font-medium max-w-md">
                      Spearheading community outreach and strategic growth. Dedicated to taking Info Payangadi to every corner of the constituency.
                    </p>
                  </div>

                  <div className="flex flex-col gap-3 w-full sm:w-auto mt-2 sm:mt-0 justify-center">
                    <a 
                      href="mailto:payangadilive@gmail.com" 
                      className="flex items-center justify-center gap-2.5 px-6 py-3.5 bg-white/5 hover:bg-white/10 backdrop-blur-md rounded-2xl transition duration-300 border border-white/10 active:scale-95"
                    >
                      <Mail size={18} className="text-indigo-300" strokeWidth={2.5} />
                      <span className="font-bold text-white text-sm tracking-wide">Contact Me</span>
                    </a>
                  </div>

                </div>
              </div>

            </motion.div>

            {/* 🔥 Footer Note with Version & Ownership 🔥 */}
            {/* <motion.div variants={itemVariants} className="text-center pt-10 pb-6 space-y-3 mt-10">
              
              <div className="w-16 h-1.5 bg-slate-200/60 rounded-full mx-auto mb-8"></div>

              <p className="font-bold text-slate-500 text-sm">
                Owned & Managed by <span className="text-blue-600 font-black tracking-wide ml-1">Payangadi Live Online</span>
              </p>
              
              <div className="flex items-center justify-center gap-3 text-xs font-semibold text-slate-400 pt-1">
                <p>Made with ❤️ in Kerala</p>
                <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                <p>Version 3.4.0 Beta</p>
              </div>
              
              <p className="text-[11px] font-medium text-slate-400 pt-2">
                © {new Date().getFullYear()} Info Payangadi. All rights reserved.
              </p>
              
            </motion.div> */}

          </motion.div>
        </div>
      </div>
    </>
  );
}